import 'promise-polyfill/src/polyfill';
import 'unfetch/polyfill';
import 'abortcontroller-polyfill';

import Vue from 'vue';
import VueRouter from 'vue-router';
import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import { CoaPlugin } from '@mgo/core';
import { bootstrapSession } from '@mgo/core/src/class/session';
import { isLocalDev } from '@mgo/core/src/environment';
import { SESSION_UNAVAILABLE_EVENT } from '@mgo/core/src/sessionState';
import segment from '@mgo/core/src/utils/segmentRudderStack';
import ExperiencesPlugin from './plugins/experiences';
import coaPersistPlugin from './plugins/coaPersist';
import App from './App.vue';
import router from './router';
import store from './store';
import getAmpersendKey from './utils/ampersend';
import gtm from './utils/gtm';
import ga from './utils/ga';
import grecaptcha from './utils/grecaptcha';
import { captureMilitaryConfirmationInvitation } from './utils/militaryConfirmationInvitation';
import VueSanitize from 'vue-sanitize';
import './css/index.css';

// URL fragments do not reach the server. Remove the one-time mailbox credential
// before analytics, session bootstrap, router hooks, or observability can inspect
// window.location, while retaining it only in this module's in-memory closure.
captureMilitaryConfirmationInvitation();

/*
 * session id for page view and create coa
 * lives for 1 day
 */
if (!Cookies.get('mgo_sid')) {
  Cookies.set('mgo_sid', uuid(), {
    expires: 1,
    sameSite: 'Strict',
    secure: !isLocalDev(window.location.origin),
  });
}

gtm();
segment();
ga();

Vue.config.productionTip = false;

Vue.use(VueSanitize,
  {
    // Options
    allowedTags: ['b', 'i', 'em', 'strong', 'a', 'p', 'div', 'span', 'table', 'tbody', 'thead', 'th', 'tr', 'td', 'ul', 'ol', 'li', 'br', 'hr'],
    allowedAttributes: {
      a: ['href'],
      img: ['src', 'alt'],
      '*': ['class', 'style', 'id', 'name', 'title', 'data-*'],
    },
  }
);
Vue.use(VueRouter);
Vue.use(coaPersistPlugin);
Vue.use(CoaPlugin, {
  api: '/api/mgo',
  origin: 'MGO',
  coaUUID: Cookies.get('coa') || '',
  orderUuid: Cookies.get('coaOrderUuid') || Cookies.get('coa') || '',
  postbackURL: `${window.location.origin}/action`,
  sid: Cookies.get('mgo_sid'),
});
Vue.use(ExperiencesPlugin, {
  key: getAmpersendKey(),
  logLevel: process.env.NODE_ENV !== 'production' ? 'error' : 'info',
  persist: false,
});

// MGOD-11701: bootstrap the BFF session before mounting. Every API call
// from here on rides on JSESSIONID; no bearer is ever in JS.
bootstrapSession()
  .catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[mgo] session bootstrap failed', err);
    const root = document.getElementById('app');
    if (root) root.innerHTML = '<p style="font-family:sans-serif;padding:2rem">Unable to start session. Please refresh the page or try again later.</p>';
    throw err;
  })
  .then(() => {
    new Vue({
      router,
      store,
      created() {
        window.addEventListener(SESSION_UNAVAILABLE_EVENT, this.handleSessionUnavailable);
        this.$coa.configGet().then((config) => {
          if (config?.rapidKeyUrl) {
            this.$store.set('config', config);
          }
          grecaptcha(config?.recaptchaEnabled || false);
        });
        this.$store.commit('mfa/SET_COA', this.$coa);
        window.addEventListener('message', (event) => {
          if (event.origin !== window.location.origin) {
            return;
          }
          window.profiling_complete(event, () => this.$store.commit('mfa/DISABLE_PROFILING'));
        }, false);
      },
      beforeDestroy() {
        window.removeEventListener(SESSION_UNAVAILABLE_EVENT, this.handleSessionUnavailable);
      },
      methods: {
        handleSessionUnavailable() {
          this.$deleteSession();
          this.$resetAppState();
          this.$coa.reset();

          if (this.$route.name !== 'session-unavailable') {
            this.$router.replace({ name: 'session-unavailable' }).catch(() => {});
          }
        },
      },
      render: (h) => h(App),
    }).$mount('#app');
  });
